import type { Editor } from '@tiptap/react';
import type {
  V1NodeExecutedInputs,
  V1NodeExecutedOutputs,
} from '@/generated/hooks/bishop/models';
import {
  extractOutputsValue,
  isBrowserInstance,
  resolveCommonValue,
  type IBrowserInstanceValue,
} from '@/modules/outputs/utils';
import { safeParseJson } from '@/shared/utils';
import { computeHighlightRanges } from '@/shared/hooks/useHighlightRanges';
import {
  convertEnglishBytesToCharacterRange,
  type ICharacterRange,
} from '@/shared/utils/source-map';
import type { V1CodeMapping } from '@/generated/hooks/grimoire/models';
import type { IFunctionCallEvent } from '../hooks/useRunJournalEvents';
import {
  getOverviewSectionHeader,
  getExecutionStepsSectionHeader,
} from '../constants';
import {
  EExecutionLogEventType,
  type IBrowserOperatorStep,
  type IEnglishCodeSection,
  type IEnglishCodeSectionWithEvents,
  type IEnglishCodeVar,
  type TExecutionLogEvent,
} from '../types';

export {
  getBookFunctionCallFromEvent,
  getAwaitingGuidanceFromEvent,
  getStatementNodeFromEvent,
  getEventCodeLocation,
  getEventOutputKeys,
  mergeConsecutiveStatementNodes,
} from './run-log-event-helpers';

/** Regex pattern to match @vars comments: <!-- @vars: [...] --> */
const VARS_COMMENT_REGEX = /<!-- @vars: (\[.*?\]) -->/g;

/**
 * Parses englishCode string into ordered sections.
 * Each section ends with a <!-- @vars: [...] --> comment.
 * Section keys are based on 0-indexed byte positions.
 *
 * @param englishCode - The english code string to parse
 * @returns Array of parsed sections in order, with byte-position keys
 *
 * @example
 * ```typescript
 * const englishCode = `1. Step one
 * <!-- @vars: [{"label": "x", "var": "x"}] -->
 *
 * 2. Step two
 * <!-- @vars: [{"label": "y", "var": "y"}] -->`;
 *
 * const sections = parseEnglishCodeSections(englishCode);
 * // Returns:
 * // [
 * //   { key: '0-52', content: '1. Step one\n<!-- @vars: [...] -->', ... },
 * //   { key: '53-106', content: '\n\n2. Step two\n<!-- @vars: [...] -->', ... }
 * // ]
 * ```
 */
export function parseEnglishCodeSections(
  englishCode: string,
): IEnglishCodeSection[] {
  if (!englishCode) {
    return [];
  }

  const sections: IEnglishCodeSection[] = [];
  let lastEndIndex = 0;
  let sectionIndex = 0;

  VARS_COMMENT_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = VARS_COMMENT_REGEX.exec(englishCode)) !== null) {
    const commentStartIndex = match.index;
    const commentEndIndex = match.index + match[0].length - 1;
    const varsJsonString = match[1];

    const vars = safeParseJson<IEnglishCodeVar[]>(varsJsonString, []) ?? [];

    const startByte = lastEndIndex;
    const content = englishCode.slice(startByte, commentEndIndex + 1);

    const rawDisplayText = englishCode.slice(startByte, commentStartIndex);
    const displayText = rawDisplayText.trim();

    const leadingWhitespaceLength =
      rawDisplayText.length - rawDisplayText.trimStart().length;
    const displayStartByte = startByte + leadingWhitespaceLength;
    const displayEndByte =
      commentStartIndex > startByte ? commentStartIndex - 1 : startByte;

    const outputs = vars.length > 0 ? vars.map(v => v.var) : null;
    const endByte = commentEndIndex;
    const key = `${startByte}-${endByte}`;

    sections.push({
      key,
      startByte,
      endByte,
      displayStartByte,
      displayEndByte,
      content,
      displayText,
      vars,
      outputs,
      index: sectionIndex,
    });

    lastEndIndex = commentEndIndex + 1;
    sectionIndex++;
  }

  return sections;
}

/**
 * List of known Commonv1Value field names.
 * Used to detect if a value is a single primitive value vs a dictionary of outputs.
 */
const COMMON_VALUE_KEYS = [
  'null_value',
  'bool_value',
  'text',
  'number',
  'date',
  'timeofday',
  'datetime',
  'time_zone',
  'duration',
  'list',
  'dictionary',
  'set',
  'table',
  'file',
] as const;

/**
 * Checks if a value is a single Commonv1Value (like null_value, text, etc.)
 * rather than a dictionary of outputs.
 *
 * A Commonv1Value has specific field names like null_value, bool_value, text, etc.
 * If the value has exactly one key and it's one of these field names, it's a single value.
 *
 * @param value - The value to check
 * @returns True if the value is a single Commonv1Value
 */
export const isSingleCommonValue = (
  value: unknown,
): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;
  const keys = Object.keys(value);
  return (
    keys.length === 1 &&
    COMMON_VALUE_KEYS.includes(keys[0] as (typeof COMMON_VALUE_KEYS)[number])
  );
};

/**
 * Generates a unique key for an event based on its type and data.
 *
 * @param event - The execution log event
 * @returns A unique key string for React list rendering
 */
export function getEventKey(event: TExecutionLogEvent): string {
  const timestamp = event.timestamp ?? '';

  switch (event.type) {
    case EExecutionLogEventType.BOOK_FUNCTION_CALL: {
      const loc = event.data.code_location;
      return `book-${timestamp}-${loc?.start_byte ?? ''}-${loc?.end_byte ?? ''}`;
    }
    case EExecutionLogEventType.AWAITING_GUIDANCE:
      return `guidance-${timestamp}-${event.data.exception ?? ''}`;
    case EExecutionLogEventType.STATEMENT_NODE: {
      const loc = event.data.code_location;
      return `statement-${timestamp}-${event.data.node_id ?? ''}-${loc?.start_byte ?? ''}`;
    }
    default:
      return `event-${timestamp}`;
  }
}

/**
 * Computes the character bounds of a paragraph in the editor's plain text space.
 * Iterates through the editor document to find the paragraph at the given index
 * and returns its start/end character offsets.
 *
 * @param editor - The TipTap editor instance
 * @param paragraphIndex - The 0-based index of the paragraph
 * @returns Object with start and end character offsets, or null if not found
 */
export function getParagraphCharBounds(
  editor: Editor | null,
  paragraphIndex: number,
): { start: number; end: number } | null {
  if (!editor) return null;
  let charOffset = 0;
  let idx = 0;
  let result: { start: number; end: number } | null = null;
  editor.state.doc.descendants(node => {
    if (result) return false;
    if (node.type.name === 'paragraph') {
      if (idx === paragraphIndex) {
        result = {
          start: charOffset,
          end: charOffset + node.textContent.length,
        };
      }
      charOffset += node.textContent.length + 1;
      idx++;
      return false;
    }
    return true;
  });
  return result;
}

/**
 * Resolves a parsed-text character position (a browser-provisioning location) to the
 * character bounds of the FIRST sub-step of the major step that contains it.
 *
 * The Browser Operator pill anchors under the major step's first sub-step (first nested
 * list paragraph) regardless of which sub-step actually provisions the browser, so the
 * provisioning location is only used to select the major step. Falls back to the major
 * step's header paragraph when it has no nested sub-steps, and to the containing
 * paragraph itself when it is not inside a list. Returns null if the editor is
 * unavailable or the position maps to no paragraph.
 *
 * Uses the same descendant-paragraph enumeration as {@link getParagraphCharBounds}, so
 * the returned bounds are in the editor's parsed-text character space.
 *
 * @param editor - The TipTap editor instance
 * @param charPos - Provisioning location in parsed-text character offsets
 * @returns Char bounds of the resolved sub-step paragraph, or null
 */
export function resolveMajorStepFirstSubStepRange(
  editor: Editor | null,
  charPos: number,
): { start: number; end: number } | null {
  if (!editor) return null;
  const doc = editor.state.doc;

  // 1. Find the paragraph whose parsed-text char span contains charPos.
  let charOffset = 0;
  let idx = 0;
  let targetIndex: number | null = null;
  let targetPos: number | null = null;
  doc.descendants((node, pos) => {
    if (targetPos !== null) return false;
    if (node.type.name === 'paragraph') {
      const len = node.textContent.length;
      if (charPos >= charOffset && charPos <= charOffset + len) {
        targetIndex = idx;
        targetPos = pos;
      }
      charOffset += len + 1;
      idx++;
      return false;
    }
    return true;
  });
  if (targetPos === null || targetIndex === null) return null;

  // 2. Walk up to the outermost listItem ancestor (the major step).
  const $target = doc.resolve(targetPos);
  let majorStart: number | null = null;
  let majorEnd: number | null = null;
  for (let depth = 1; depth <= $target.depth; depth++) {
    if ($target.node(depth).type.name === 'listItem') {
      majorStart = $target.before(depth);
      majorEnd = majorStart + $target.node(depth).nodeSize;
      break;
    }
  }
  // Not inside a list → anchor to the paragraph itself.
  if (majorStart === null || majorEnd === null) {
    return getParagraphCharBounds(editor, targetIndex);
  }
  const stepStart = majorStart;
  const stepEnd = majorEnd;

  // 3. Within the major step, find the first nested sub-step paragraph (one whose
  //    nearest listItem differs from the major listItem). Track the header as a
  //    fallback. idx2 is the global paragraph index, matching getParagraphCharBounds.
  let idx2 = 0;
  let headerIndex: number | null = null;
  let subStepIndex: number | null = null;
  doc.descendants((node, pos) => {
    if (subStepIndex !== null) return false;
    if (node.type.name === 'paragraph') {
      if (pos > stepStart && pos < stepEnd) {
        if (headerIndex === null) headerIndex = idx2;
        const $p = doc.resolve(pos);
        let nearestListItem: number | null = null;
        for (let d = $p.depth; d > 0; d--) {
          if ($p.node(d).type.name === 'listItem') {
            nearestListItem = $p.before(d);
            break;
          }
        }
        if (nearestListItem !== null && nearestListItem !== stepStart) {
          subStepIndex = idx2;
        }
      }
      idx2++;
      return false;
    }
    return true;
  });

  // headerIndex is always set once we are inside a list (the major step has at
  // least its own header paragraph), so the final `?? targetIndex` is only a
  // type-level guarantee of a non-null index and is not reachable at runtime here.
  return getParagraphCharBounds(
    editor,
    subStepIndex ?? headerIndex ?? targetIndex,
  );
}

/**
 * Computes the raw markdown offset for the prefix before the AOP code
 * in the combined editor content. This accounts for Overview and Execution Steps headers.
 *
 * @param description - The automation description
 * @param aopCode - The sanitized AOP code
 * @param includeSectionHeaders - Whether the combined content includes the
 *   Overview/Execution Steps headers. When false (Quill V2), the AOP code is
 *   rendered at offset 0 with no prefix.
 * @returns The character offset of the AOP code within the combined content
 */
export function computeRawMarkdownOffset(
  description?: string,
  aopCode?: string,
  includeSectionHeaders = true,
): number {
  if (!aopCode || aopCode.trim() === '') {
    return 0;
  }

  // Quill V2: AOP rendered verbatim with no section-header prefix.
  if (!includeSectionHeaders) {
    return 0;
  }

  const executionStepsHeader = getExecutionStepsSectionHeader();

  if (!description || description.trim() === '') {
    return `${executionStepsHeader}\n`.length;
  }

  const overviewHeader = getOverviewSectionHeader();
  return `${overviewHeader}\n${description}\n${executionStepsHeader}\n`.length;
}

/**
 * Returns true when any resolved value in a payload is a browser instance.
 * Used by section-level scans to check inputs and outputs uniformly.
 */
export function payloadHasBrowserInstance(
  payload: V1NodeExecutedInputs | V1NodeExecutedOutputs | undefined,
): boolean {
  if (!payload) return false;
  const resolved = extractOutputsValue(payload);
  return Object.values(resolved).some(isBrowserInstance);
}

/**
 * True if the section's most recent statement-node has a browser instance
 * in inputs or outputs. Browser-action steps receive the browser as an input
 * and emit only a `browser_id` string in outputs, hence the dual scan.
 */
export function sectionHasBrowserInstance(
  section: IEnglishCodeSectionWithEvents,
): boolean {
  for (let i = section.events.length - 1; i >= 0; i--) {
    const event = section.events[i];
    if (event.type !== EExecutionLogEventType.STATEMENT_NODE) continue;
    return (
      payloadHasBrowserInstance(event.data.inputs) ||
      payloadHasBrowserInstance(event.data.outputs)
    );
  }
  return false;
}

/** Value counterpart to {@link payloadHasBrowserInstance}. */
export function extractPayloadBrowserInstance(
  payload: V1NodeExecutedInputs | V1NodeExecutedOutputs | undefined,
): IBrowserInstanceValue | undefined {
  if (!payload) return undefined;
  const resolved = extractOutputsValue(payload);
  const found = Object.values(resolved).find(isBrowserInstance);
  return found as IBrowserInstanceValue | undefined;
}

/**
 * Value counterpart to {@link sectionHasBrowserInstance}.
 *
 * Scans the section's events in reverse, so for a multi-event section it returns
 * the instance from the *last* `STATEMENT_NODE` (which may be a step that uses the
 * browser rather than the one that provisioned it). That is intentional and safe
 * here: every event in the section references the same browser instance, and the
 * pill's placement is resolved structurally (see RunPage `browserAnchors` →
 * `resolveMajorStepFirstSubStepRange`), not from which event this returns. A
 * future caller that needs the *provisioning* event specifically should not rely
 * on this "last event wins" behavior.
 */
export function extractSectionBrowserInstance(
  section: IEnglishCodeSectionWithEvents,
): IBrowserInstanceValue | undefined {
  for (let i = section.events.length - 1; i >= 0; i--) {
    const event = section.events[i];
    if (event.type !== EExecutionLogEventType.STATEMENT_NODE) continue;
    return (
      extractPayloadBrowserInstance(event.data.inputs) ??
      extractPayloadBrowserInstance(event.data.outputs)
    );
  }
  return undefined;
}

interface IComputeBrowserOperatorStepsParams {
  /** Whether this is a Quill V2 run (driven by the execution journal). */
  isQuillV2: boolean;
  /** Quill V2 function-call events. */
  journalEvents: IFunctionCallEvent[];
  /** Legacy grouped sections. */
  groupedSections: IEnglishCodeSectionWithEvents[];
  /** Code mapping for spy→aop conversion (Quill V2 path). */
  codeMapping: V1CodeMapping | null | undefined;
  /** The aop/english code text. */
  aopText: string | undefined;
}

/**
 * Returns one entry per distinct browser session, anchored to the first step
 * (in source-byte order) that used that session. The runtime passes the
 * browser as an *input* to every subsequent browser-action step, so dedup by
 * session key keeps a single anchor at the provisioning step.
 *
 * Ranges are in aop-code coordinates; the caller maps them to editor
 * (parsed-text) coordinates. Returns `[]` when the run uses no browser. This
 * is the data half of the inline Browser Operator pill — positioning is the
 * caller's job (see RunPage `setAnchors`).
 */
export function computeBrowserOperatorSteps({
  isQuillV2,
  journalEvents,
  groupedSections,
  codeMapping,
  aopText,
}: IComputeBrowserOperatorStepsParams): IBrowserOperatorStep[] {
  if (!aopText) return [];

  const steps: IBrowserOperatorStep[] = [];
  const seenSessions = new Set<string>();

  const addSession = (
    value: IBrowserInstanceValue,
    aopRanges: ICharacterRange[],
  ): void => {
    const sessionKey = value.session_id || value.browser_name || '';
    if (!sessionKey || seenSessions.has(sessionKey)) return;
    if (aopRanges.length === 0) return;
    seenSessions.add(sessionKey);
    steps.push({ sessionKey, value, aopRanges });
  };

  if (isQuillV2) {
    // Order by call-site byte start so the first occurrence is the invoking step.
    const orderedEvents = [...journalEvents]
      .filter(event => event.codeLocation)
      .sort(
        (a, b) =>
          Number(a.codeLocation?.start_byte ?? 0) -
          Number(b.codeLocation?.start_byte ?? 0),
      );
    for (const event of orderedEvents) {
      // The browser instance comes from the call's inputs or its return value
      // (IFunctionCallEvent exposes the result as `returnValue`, a Commonv1Value).
      let value = extractPayloadBrowserInstance(event.inputs);
      if (!value && event.returnValue != null) {
        const resolved = resolveCommonValue(event.returnValue);
        if (isBrowserInstance(resolved)) {
          value = resolved;
        }
      }
      if (!value || !event.codeLocation) continue;
      const { ranges } = computeHighlightRanges({
        location: event.codeLocation,
        codeMapping,
        aopText,
      });
      addSession(value, ranges);
    }
  } else {
    // Sort by byte position so the first occurrence wins dedup, matching the
    // Quill V2 path above (sections are normally already in document order, but
    // don't rely on the caller for that).
    const orderedSections = [...groupedSections].sort(
      (a, b) => a.displayStartByte - b.displayStartByte,
    );
    for (const section of orderedSections) {
      const value = extractSectionBrowserInstance(section);
      if (!value) continue;
      const range = convertEnglishBytesToCharacterRange({
        startByte: section.displayStartByte,
        endByte: section.displayEndByte,
        aopText,
      });
      addSession(value, range ? [range] : []);
    }
  }

  return steps;
}
